import {
  ArrowBack,
  ArrowForward,
  DocumentScanner,
  ExpandMore,
  NotStarted,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  FieldAutofillHistory,
  useDataMaidEntityActions,
  useFieldAutofillStatus,
} from 'api/datamaid';
import { EndpointUrl } from 'api/endpoint';
import { useEntity } from 'api/entity';
import { useCurrentUser } from 'api/providers/authProvider';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { CheckboxWithLabel } from 'components/FieldEditor/components';
import { useCurrentModal, useModal } from 'components/modal';
import {
  DragFieldUpdateModal,
  DragFieldUpdateModalAction,
} from 'components/ProjectsList/droppableList';
import { FinsightEntityFieldValue } from 'components/renderField';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';
import { Stateful } from 'types/react';
import { formatName } from 'utils/fields';

type DataMaidEntityMenuProps = {
  anchorEl: HTMLElement | null;
  setAnchorEl: Stateful<HTMLElement | null>;
  entities: (FinsightEntity | string)[];
  onCloseParentMenu?: () => void;
};

function useResolvedEntities(entities: (FinsightEntity | string)[]) {
  const singleStringId =
    entities.length === 1 && typeof entities[0] === 'string'
      ? entities[0]
      : null;
  const singleEntityFetch = useEntity(singleStringId ?? undefined);

  if (entities.length === 0) {
    return { data: null as FinsightEntity[] | null, isLoading: false };
  }

  const allObjects = entities.every((entity) => typeof entity !== 'string');
  if (allObjects) {
    return {
      data: entities as FinsightEntity[],
      isLoading: false,
    };
  }

  if (entities.length === 1 && typeof entities[0] === 'string') {
    return {
      data: singleEntityFetch.data ? [singleEntityFetch.data] : null,
      isLoading: singleEntityFetch.isLoading,
    };
  }

  const resolved = entities.filter(
    (entity): entity is FinsightEntity => typeof entity !== 'string',
  );

  return {
    data: resolved.length > 0 ? resolved : null,
    isLoading: false,
  };
}

export function DataMaidEntityMenu(props: DataMaidEntityMenuProps) {
  const { anchorEl, setAnchorEl } = props;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
    >
      <DataMaidEntityMenuItems {...props} />
    </Menu>
  );
}

function DataMaidEntityMenuItems(props: DataMaidEntityMenuProps) {
  const { setAnchorEl, onCloseParentMenu } = props;
  const entityData = useResolvedEntities(props.entities);
  const entities = entityData.data ?? [];
  const entity = entities[0];
  const isMultiple = entities.length > 1;
  const autofillStatus = useFieldAutofillStatus(entity?.id ?? null);

  const { batchManualfillEntityFields } = useDataMaidEntityActions();

  const { showModal } = useModal();

  const triggerBatchManualfill = (
    fields: string[],
    instructions: string,
    websearch: boolean,
  ) => {
    batchManualfillEntityFields(
      entities.map((item) => ({
        entityId: item.id,
        fields,
        instructions,
        websearch,
      })),
    );
  };

  if (entityData.isLoading || !entity) {
    return (
      <ListItem>
        <ListItemText primary="Loading..." />
        <CircularProgress size={24} sx={{ ml: 3 }} />
      </ListItem>
    );
  }

  const AutofillFormModal = () => {
    const modalCtx = useCurrentModal();
    const [instructions, setInstructions] = useState('');
    const [selectedFields, setSelectedFields] = useState<Set<string>>(
      new Set(),
    );
    const [useWebSearch, setUseWebSearch] = useState(false);

    return (
      <Stack gap={2} maxWidth={500}>
        <Typography variant="h6">
          {isMultiple ? `Autofill ${entities.length} entities` : entity.name}
        </Typography>
        {isMultiple && (
          <Typography variant="body2" color="text.secondary">
            Field selection applies to all selected entities.
          </Typography>
        )}
        <TextField
          multiline
          rows={8}
          label="Instructions (optional)"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            {selectedFields.size === 0
              ? 'Autofill will update all fields'
              : `Autofill will update ${selectedFields.size} fields`}
          </AccordionSummary>
          <AccordionDetails>
            <Stack width="100%" direction="row" flexWrap="wrap" gap={1}>
              <CheckboxWithLabel
                label="Name"
                description={entity.name}
                checked={selectedFields.has('name')}
                setChecked={(v) =>
                  setSelectedFields((prev) => {
                    const newSet = new Set(prev);
                    if (v) {
                      newSet.add('name');
                    } else {
                      newSet.delete('name');
                    }
                    return newSet;
                  })
                }
              />
              <CheckboxWithLabel
                label="Description"
                description={entity.description}
                checked={selectedFields.has('description')}
                setChecked={(v) =>
                  setSelectedFields((prev) => {
                    const newSet = new Set(prev);
                    if (v) {
                      newSet.add('description');
                    } else {
                      newSet.delete('description');
                    }
                    return newSet;
                  })
                }
              />
              {Object.entries(entity.extraFieldsMeta).map(([name]) => (
                <CheckboxWithLabel
                  key={name}
                  label={formatName(name)}
                  description={
                    <FinsightEntityFieldValue
                      field={entity.extraFieldsMeta[name]}
                      value={entity.extraFields[name]}
                      viewType="table"
                      editable={false}
                    />
                  }
                  checked={selectedFields.has(name)}
                  setChecked={(v) =>
                    setSelectedFields((prev) => {
                      const newSet = new Set(prev);
                      if (v) {
                        newSet.add(name);
                      } else {
                        newSet.delete(name);
                      }
                      return newSet;
                    })
                  }
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <CheckboxWithLabel
          label="Use web search"
          description={
            <>
              Performs a web search and provides the context to DataMaid to
              ensure the latest and most complete information is used.
            </>
          }
          checked={useWebSearch}
          setChecked={(v) => setUseWebSearch(v)}
        />
        <Button
          onClick={() => {
            triggerBatchManualfill(
              Array.from(selectedFields),
              instructions,
              useWebSearch,
            );
            modalCtx?.closeModal();
          }}
        >
          Autofill
        </Button>
      </Stack>
    );
  };

  return [
    <Stack key="autofill-with-datamaid" direction="row">
      <IconButton
        disabled={!!autofillStatus.data?.length}
        sx={{
          px: 2,
        }}
        onClick={async () => {
          setAnchorEl(null);
          onCloseParentMenu?.();

          triggerBatchManualfill([], '', true);
        }}
      >
        <NotStarted />
      </IconButton>
      <Divider orientation="vertical" flexItem />
      <ListItemButton
        disabled={!!autofillStatus.data?.length}
        onClick={async () => {
          setAnchorEl(null);
          onCloseParentMenu?.();

          showModal(
            isMultiple
              ? `Autofill ${entities.length} entities with DataMaid`
              : 'Autofill with DataMaid',
            <AutofillFormModal />,
          );
        }}
      >
        <ListItemIcon>
          <DocumentScanner />
        </ListItemIcon>
        <ListItemText
          primary={
            isMultiple
              ? `Autofill with DataMaid (${entities.length})`
              : 'Autofill with DataMaid'
          }
        />
      </ListItemButton>
    </Stack>,
    !!autofillStatus.data?.length &&
      (autofillStatus.data[0].status === 'pending' ||
        autofillStatus.data[0].status === 'processing') && (
        <>
          <Divider />
          <ListSubheader
            sx={{
              lineHeight: '32px',
            }}
          >
            In Progress
          </ListSubheader>
          <ListItem>
            <ListItemIcon>
              <DocumentScanner />
            </ListItemIcon>
            <ListItemText
              primary="Autofill in progress..."
              secondary={moment(autofillStatus.data[0].created_at).fromNow()}
            />
            <CircularProgress
              size={20}
              sx={{
                ml: 2,
              }}
            />
          </ListItem>
        </>
      ),
    !!autofillStatus.data?.length &&
      autofillStatus.data[0].status === 'review' && (
        <>
          <Divider />
          <ListSubheader
            sx={{
              lineHeight: '32px',
            }}
          >
            Needs Review
          </ListSubheader>
          <ListItemButton
            onClick={async () => {
              setAnchorEl(null);
              const autofillData = autofillStatus.data?.[0];
              showModal(
                'Review DataMaid Field Autofill',
                <DataMaidReviewAutofillModal
                  autofillData={autofillData!}
                  entity={entity}
                />,
              );
            }}
          >
            <ListItemIcon>
              <DocumentScanner />
            </ListItemIcon>
            <ListItemText primary="Review Field Autofill Suggestions" />

            <Box
              sx={{
                display: 'flex',
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'red',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: 14,
                fontWeight: 'bold',
                ml: 1,
              }}
            >
              {1}
            </Box>
          </ListItemButton>
        </>
      ),
  ];
}

function DataMaidReviewAutofillModal(props: {
  autofillData: FieldAutofillHistory;
  entity: FinsightEntity;
}) {
  const { entity, autofillData } = props;
  const workspaceId = useWorkspaceId();
  const currentUser = useCurrentUser();
  const { cache, mutate } = useSWRConfig();
  const modal = useCurrentModal();

  const actions = useMemo(() => {
    const actions: DragFieldUpdateModalAction[] = [];

    for (const key in autofillData.new_fields) {
      if (key in entity.extraFieldsMeta) {
        actions.push({
          field: key,
          value: autofillData.new_fields[key],
        });
      } else if (key === 'name' || key === 'description') {
        actions.push({
          field: key,
          value: autofillData.new_fields[key],
        });
      }
    }

    return actions;
  }, [autofillData.new_fields, entity.extraFieldsMeta]);

  return (
    <>
      <Typography variant="h6">{entity.name}</Typography>
      <Typography>{entity.description?.slice(0, 100)}</Typography>
      <Typography>
        Completed: {moment(autofillData?.processed_at).calendar()}
      </Typography>
      <br />
      <DragFieldUpdateModal
        allOptional
        defaultSelectAll
        entity={entity}
        actions={actions}
        onUpdated={async () => {
          if (!currentUser?.token) return;

          await fetch(
            `${EndpointUrl}/workspace/${workspaceId}/datamaid/field-autofill/${entity.id}/${autofillData.id}/dismiss`,
            {
              method: 'POST',
              headers: {
                'X-Finsight-Token': currentUser?.token,
              },
            },
          );

          Array.from(cache.keys())
            .filter((x) =>
              x.includes(
                `/workspace/${workspaceId}/datamaid/field-autofill/${entity.id}/status`,
              ),
            )
            .forEach((key) => mutate(key));

          modal?.closeModal();
        }}
      />
    </>
  );
}

export function DataMaidReviewAutofillCarouselModal(props: {
  reviewItems: FieldAutofillHistory[];
}) {
  const { reviewItems: _reviewItems } = props;
  const [reviewItems, setReviewItems] =
    useState<FieldAutofillHistory[]>(_reviewItems);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const workspaceId = useWorkspaceId();
  const currentUser = useCurrentUser();
  const { cache, mutate } = useSWRConfig();
  const modal = useCurrentModal();

  const currentItem = reviewItems[currentIndex];
  const entityData = useEntity(currentItem?.entity_id);
  const entity = entityData.data;

  useEffect(() => {
    setReviewItems(_reviewItems);
  }, [_reviewItems]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(reviewItems.length - 1, prev + 1));
  };

  const handleDismiss = async () => {
    if (!currentUser?.token || !entity || !currentItem) return;

    await fetch(
      `${EndpointUrl}/workspace/${workspaceId}/datamaid/field-autofill/${entity.id}/${currentItem.id}/dismiss`,
      {
        method: 'POST',
        headers: {
          'X-Finsight-Token': currentUser?.token,
        },
      },
    );

    Array.from(cache.keys())
      .filter(
        (x) =>
          x.includes(
            `/workspace/${workspaceId}/datamaid/field-autofill/${entity.id}/status`,
          ) ||
          x.includes(
            `/workspace/${workspaceId}/datamaid/history/field-autofill`,
          ),
      )
      .forEach((key) => mutate(key));

    // Move to next item, or previous if at the end, or close if only one item
    if (reviewItems.length === 1) {
      modal?.closeModal();
    } else {
      setReviewItems(reviewItems.filter((_, index) => index !== currentIndex));
      // At the end, move to previous item
      setCurrentIndex((prev) =>
        Math.max(0, Math.min(prev, reviewItems.length - 1)),
      );
    }
  };

  const actions = useMemo(() => {
    if (!entity) return [];
    const actions: DragFieldUpdateModalAction[] = [];

    for (const key in currentItem.new_fields) {
      if (key in entity.extraFieldsMeta) {
        actions.push({
          field: key,
          value: currentItem.new_fields[key],
        });
      }
    }

    return actions;
  }, [currentItem.new_fields, entity]);

  if (entityData.isLoading || !entity || !currentItem) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box key={currentIndex}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <IconButton
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          aria-label="Previous"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {currentIndex + 1} of {reviewItems.length}
        </Typography>
        <IconButton
          onClick={handleNext}
          disabled={currentIndex === reviewItems.length - 1}
          aria-label="Next"
        >
          <ArrowForward />
        </IconButton>
      </Stack>

      <Box mb={2}>
        <Typography variant="h6">{entity.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {entity.description?.slice(0, 100)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Completed: {moment(currentItem?.processed_at).calendar()}
        </Typography>
      </Box>

      <DragFieldUpdateModal
        allOptional
        defaultSelectAll
        entity={entity}
        actions={actions}
        onUpdated={handleDismiss}
      />
    </Box>
  );
}
